import { colors } from '@fitpet/constants-global'
import { FitpetIcon } from '@fitpet/ui/components'
import { useSelector } from 'react-redux'
import styled, { useTheme } from 'styled-components'
import { ImageAsset } from '@/components/common'
import ToolTip from '@/components/common/toolTips/ToolTip'
import { Typo, TypoVariant } from '@/components/common/typography'
import ROUTES from '@/constants/legacy/constRoutes'
import { PathToLocation } from '@/constants/location.enum'
import { QueryStringKeyEnum } from '@/constants/query-string-key.enum'
import { TooltipArrowPositionEnum } from '@/constants/tooltip-arrow-position.enum'
import { useCustomRouter } from '@/containers/hooks'
import FitpetPlusCouponLogo from '@/images/fitpet_plus_coupon_logo.svg'
import { RootState } from '@/stores/store'
import { currencyText } from '@/utils/utilNumber'

export const CartSummary = () => {
  const { color } = useTheme()
  const { push, pathname } = useCustomRouter()
  const cartStorageData = useSelector((state: RootState) => state.reduxDataReducers.cartData)

  const handleClickMembershipIntro = () => {
    push({
      pathname: ROUTES.MEMBERSHIP.INTRO,
      query: {
        [QueryStringKeyEnum.Location]: PathToLocation[pathname],
      },
    })
  }

  if (!cartStorageData) {
    return null
  }

  const { productAmount, amount, isMembership, shippingAmount, expectedMileage } = cartStorageData

  return (
    <StyledTotalSummary>
      <div className="total-cart-summary">
        <PriceWithFitpetPlus>
          <div className="total-cart-summary__list">
            <div className="total-cart-summary__item">
              <Typo variant={TypoVariant.Body3Regular} color={color.gray500}>
                총 주문 금액
              </Typo>
              <Typo variant={TypoVariant.Body3SemiBold}>{currencyText(productAmount)}</Typo>
            </div>
            <div className="total-cart-summary__item">
              <Typo variant={TypoVariant.Body3Regular} color={color.gray500}>
                총 배송비
              </Typo>
              {isMembership ? (
                <div className="total-cart-summary__membership-free">
                  <StyledFitpetPlusCouponLogo />
                  <Typo variant={TypoVariant.Body3SemiBold}>무료배송</Typo>
                </div>
              ) : (
                <Typo variant={TypoVariant.Body3SemiBold}>{currencyText(shippingAmount)}</Typo>
              )}
            </div>
          </div>
        </PriceWithFitpetPlus>
        <div>
          <div className="total-cart-summary__total-price">
            <Typo variant={TypoVariant.Body1Bold}>총 결제 금액</Typo>
            <Typo variant={TypoVariant.Heading5}>{currencyText(amount)}</Typo>
          </div>
          <div className="total-cart-summary__review">
            {isMembership ? (
              <div className="total-cart-summary__membership-mileage">
                <StyledFitpetPlusCouponLogo />
                <Typo variant={TypoVariant.Body3Regular} color={color.gray500}>
                  최대 적립
                </Typo>
                <ToolTip
                  tooltipData={<Typo variant={TypoVariant.Body3Regular}>핏펫플러스 + 구매확정 + 리뷰 작성 시</Typo>}
                  tooltipStyle={{ top: '-4.5rem', transform: 'translateX(-46%)' }}
                  arrowPosition={TooltipArrowPositionEnum.BottomMiddle}
                >
                  <FitpetPlusInfoIconContainer>
                    <ImageAsset name="ic_question_gray300_16" size={16} svg />
                  </FitpetPlusInfoIconContainer>
                </ToolTip>
              </div>
            ) : (
              <Typo variant={TypoVariant.Body3Regular} color={color.gray500}>
                구매 확정 + 리뷰 작성 시 최대 적립
              </Typo>
            )}
            <Typo variant={TypoVariant.Body3Bold}>{currencyText(expectedMileage)}</Typo>
          </div>
        </div>
      </div>
    </StyledTotalSummary>
  )
}

const StyledFitpetPlusCouponLogo = styled(FitpetPlusCouponLogo)`
  width: 6.8rem;
  height: 1.8rem;
`

const StyledTotalSummary = styled.div`
  .total-cart-summary {
    display: flex;
    flex-direction: column;
    padding: 1.6rem;
    &__list {
      display: flex;
      flex-direction: column;
      gap: 0.6rem;
    }
    &__membership-free {
      display: flex;
      gap: 0.4rem;
      align-items: center;
    }
    &__item {
      display: flex;
      justify-content: space-between;
    }
    &__item-inner {
      display: flex;
      justify-content: space-between;
      margin-left: 0.8rem;
    }
    &__total-price {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.6rem;
    }
    &__review {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    &__membership-mileage {
      display: flex;
      gap: 0.4rem;
      align-items: center;
    }
  }
`

const StyledFitpetPlus = styled.div`
  margin-top: 1.2rem;
  display: flex;
  cursor: pointer;
  justify-content: space-between;
  align-items: center;
  padding: 1.4rem 1.2rem;
  border-radius: 8px;
  border: ${({
    theme: {
      color: { gray200 },
    },
  }) => `1px solid ${gray200}`};
  .fitpet-plus-title {
    display: flex;
    gap: 0.4rem;
    align-items: center;
  }
  .fitpet-plus-text-button {
    display: flex;
    gap: 0.4rem;
    align-items: center;
  }
`

const FitpetPlusInfoIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const PriceWithFitpetPlus = styled.div`
  margin-bottom: 1.6rem;
`
